import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { usePageSeo } from "@/lib/usePageSeo";
import { useJsonLd, buildCreativeWorkSchema, buildVideoObjectSchema } from "@/lib/seoSchema";
import { ArrowLeft, ChevronRight } from "lucide-react";
import { Reveal } from "@/components/util/Reveal";
import { BrandedLoader } from "@/components/util/BrandedLoader";
import { PortfolioMediaTile, resolveGallery } from "@/components/portfolio/PortfolioMediaTile";
import { ProjectVideoPlayer } from "@/components/portfolio/ProjectVideoPlayer";

/**
 * Case-study destination page — Portfolio Mixed-Media V1 (Feb 2026).
 *
 *   MINIMAL WRITING. MAXIMUM VISUAL IMPACT.
 *
 * The hero keeps project context concise (title, category, client,
 * optional short description). Below the hero, the mixed-media gallery
 * dominates — images render at natural aspect, landscape videos take
 * full-width rows, portraits and squares pair side-by-side on wider
 * screens. Legacy image-only projects render unchanged via the
 * `resolveGallery` reader fallback.
 *
 * The long-form Case Study narrative sections + Services Used blocks
 * are intentionally NOT rendered — the visuals do the selling. Legacy
 * `case_study_sections` data stays in the DB (never destructively
 * removed), just no longer surfaces publicly.
 */
export default function PortfolioDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [siblings, setSiblings] = useState({ prev: null, next: null });
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setProject(null);
    setNotFound(false);
    (async () => {
      try {
        const [pRes, listRes] = await Promise.all([
          api.get(`/portfolio/${slug}`),
          api.get(`/portfolio`),
        ]);
        if (cancelled) return;
        setProject(pRes.data);
        const list = listRes.data || [];
        const idx = list.findIndex((p) => (p.slug || p.id) === (pRes.data.slug || pRes.data.id));
        if (idx !== -1) {
          setSiblings({
            prev: idx > 0 ? list[idx - 1] : list[list.length - 1],
            next: idx < list.length - 1 ? list[idx + 1] : list[0],
          });
        }
      } catch (e) {
        if (!cancelled) setNotFound(true);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const rawGallery = useMemo(() => resolveGallery(project), [project]);

  usePageSeo({
    title: project ? `${project.title} — Portfolio — Ur Designs` : undefined,
    description: project
      ? [project.client, project.category, "Case study by Ur Designs"].filter(Boolean).join(" — ")
      : undefined,
    path: project ? `/portfolio/${project.slug || project.id}` : undefined,
    image: project?.image || undefined,
    noindex: notFound,
  });
  useJsonLd(buildCreativeWorkSchema(project));
  useJsonLd(buildVideoObjectSchema(project));
  // Portfolio Image Aspect-Ratio Fix — images don't ship with a stored
  // orientation on legacy projects (Calvin never picked one; the audit
  // explicitly forbids adding a new required CMS field). Measure each
  // image's intrinsic dimensions at render time and reclassify. Videos
  // keep the admin's stored orientation as authority — the CMS
  // Save-time gate (Portfolio Mixed Portrait Pair Fix, Feb 2026)
  // guarantees the persisted video orientation matches its actual
  // shape, so no runtime measurement is required for videos. Ready
  // flag gates the gallery so we don't render a wrong grouping first
  // and shift on measurement.
  const [imgOrientations, setImgOrientations] = useState({});
  const [galleryReady, setGalleryReady] = useState(false);
  useEffect(() => {
    if (!rawGallery || rawGallery.length === 0) { setGalleryReady(true); return; }
    const urls = rawGallery
      .filter((a) => a.kind === "image" && a.url)
      .map((a) => a.url);
    if (urls.length === 0) { setGalleryReady(true); return; }
    let cancelled = false;
    const acc = {};
    let done = 0;
    const finish = () => {
      if (cancelled) return;
      done += 1;
      if (done === urls.length) {
        setImgOrientations(acc);
        setGalleryReady(true);
      }
    };
    urls.forEach((url) => {
      const img = new Image();
      img.onload = () => {
        const w = img.naturalWidth || 1;
        const h = img.naturalHeight || 1;
        const ratio = w / h;
        // Threshold rules: wide-shaped stays landscape, near-square is
        // its own class, anything visibly taller than wide is portrait.
        acc[url] = ratio >= 1.3 ? "landscape" : ratio < 0.85 ? "portrait" : "square";
        finish();
      };
      img.onerror = () => {
        acc[url] = "landscape"; // safe fallback — legacy assumption
        finish();
      };
      img.src = url;
    });
    return () => { cancelled = true; };
  }, [rawGallery]);
  const gallery = useMemo(
    () => rawGallery.map((a) => (
      a.kind === "image" && imgOrientations[a.url]
        ? { ...a, orientation: imgOrientations[a.url] }
        : a
    )),
    [rawGallery, imgOrientations],
  );
  const rows = useMemo(() => groupGalleryRows(gallery), [gallery]);

  if (notFound) {
    return (
      <div className="bg-[#0F0F0F] text-white min-h-[60vh] grid place-items-center">
        <div className="text-center px-6">
          <div className="text-[#FF6600] text-xs font-bold tracking-[0.2em] uppercase">Not Found</div>
          <h1 className="font-display mt-2 text-4xl sm:text-5xl font-black tracking-tight">Project Not Found</h1>
          <Link to="/portfolio" className="mt-6 inline-flex items-center gap-2 text-[#FF6600] font-semibold hover:gap-3 transition-all">
            <ArrowLeft className="w-4 h-4" /> Back to Portfolio
          </Link>
        </div>
      </div>
    );
  }

  if (!project) {
    return <BrandedLoader testid="portfolio-detail-loading" />;
  }

  return (
    <div data-testid="portfolio-detail-page" className="bg-[#0F0F0F] text-white">
      {/* HERO — Portfolio Visual Refinement V1.
          Wide container remains, but the cover image now renders in two
          layers so arbitrary aspect ratios (16:9, square, portrait,
          packaging) look intentional rather than aggressively cropped:
            • Layer 1 — enlarged, blurred, darkened background fill.
            • Layer 2 — the same image in `object-contain` so nothing
              important is cropped.
          One Cover upload works for every project shape; Calvin never
          has to build a bespoke banner. */}
      <section className="relative">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-8 sm:pt-10">
          <Link
            to="/portfolio"
            data-testid="detail-back-link"
            className="inline-flex items-center gap-2 text-sm text-[#A3A3A3] hover:text-[#FF6600] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> All Projects
          </Link>
        </div>
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 mt-6">
          <div
            data-testid="detail-hero"
            className="relative rounded-3xl overflow-hidden aspect-[4/3] sm:aspect-[16/9] lg:aspect-[21/9] bg-[#0B0B0B] border border-[#262626]"
          >
            {/* Layer 1 — blurred fill so wide containers never show empty bars */}
            <img
              src={project.image}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-40"
            />
            {/* Layer 2 — contain-fit foreground preserves the actual artwork */}
            <img
              src={project.image}
              alt={project.title}
              data-testid="detail-hero-fg"
              className="absolute inset-0 w-full h-full object-contain"
            />
            {/* Bottom gradient for text legibility over any cover */}
            <div className="absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t from-[#0F0F0F] via-[#0F0F0F]/70 to-transparent pointer-events-none" />
            <div className="absolute inset-x-0 bottom-0 p-5 sm:p-8 lg:p-10">
              {project.category && (
                <div className="text-[#FF6600] text-[10px] sm:text-xs font-bold tracking-[0.2em] uppercase">
                  {project.category}
                </div>
              )}
              <h1
                data-testid="detail-title"
                className="font-display mt-2 text-2xl sm:text-4xl lg:text-6xl font-black tracking-tight leading-[1.05]"
              >
                {project.title}
              </h1>
              {project.client && (
                <div className="mt-2 text-xs sm:text-sm text-[#B5B5B5]">
                  <span className="text-[#A3A3A3]">Client:</span>{" "}
                  <span className="font-semibold text-white">{project.client}</span>
                </div>
              )}
              {project.description && (
                <p
                  data-testid="detail-description"
                  className="mt-3 text-xs sm:text-sm lg:text-base text-[#D1D1D1] max-w-2xl leading-relaxed"
                >
                  {project.description}
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      <ProjectVideoPlayer project={project} />

      {/* MIXED-MEDIA GALLERY — row-based so narrow assets pair or center */}
      {galleryReady && rows.length > 0 && (
        <section data-testid="detail-gallery" className="bg-[#0B0B0B]">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-14 sm:py-20 space-y-6 sm:space-y-8">
            {rows.map((row, ri) => (
              <GalleryRow key={ri} row={row} projectTitle={project.title} rowIndex={ri} />
            ))}
          </div>
        </section>
      )}

      {/* PREV / NEXT PROJECT */}
      {(siblings.prev || siblings.next) && (
        <section className="border-t border-[#1A1A1A]">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-10 grid sm:grid-cols-2 gap-4">
            {siblings.prev && (
              <button
                type="button"
                data-testid="detail-prev-project"
                onClick={() => navigate(`/portfolio/${siblings.prev.slug || siblings.prev.id}`)}
                className="group text-left rounded-2xl border border-[#262626] bg-[#141414] hover:border-[#FF6600]/60 transition-colors overflow-hidden flex items-center gap-4 p-4"
              >
                <div className="shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-[#0F0F0F]">
                  <img src={siblings.prev.image} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#A3A3A3] font-bold flex items-center gap-1.5">
                    <ArrowLeft className="w-3 h-3" /> Previous Project
                  </div>
                  <div className="font-display text-lg font-bold truncate mt-1">{siblings.prev.title}</div>
                </div>
              </button>
            )}
            {siblings.next && (
              <button
                type="button"
                data-testid="detail-next-project"
                onClick={() => navigate(`/portfolio/${siblings.next.slug || siblings.next.id}`)}
                className="group text-right rounded-2xl border border-[#262626] bg-[#141414] hover:border-[#FF6600]/60 transition-colors overflow-hidden flex items-center gap-4 p-4 sm:justify-end"
              >
                <div className="min-w-0 flex-1 order-1 sm:order-none">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#A3A3A3] font-bold flex items-center gap-1.5 justify-end">
                    Next Project <ChevronRight className="w-3 h-3" />
                  </div>
                  <div className="font-display text-lg font-bold truncate mt-1">{siblings.next.title}</div>
                </div>
                <div className="shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-[#0F0F0F]">
                  <img src={siblings.next.image} alt="" className="w-full h-full object-cover" />
                </div>
              </button>
            )}
          </div>
        </section>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Portfolio Image Aspect-Ratio Fix (Feb 2026).
//
// Deterministic row grouping now respects the natural shape of BOTH
// videos AND images. Any non-landscape asset (portrait or square) is
// treated as narrow and either pairs with the next same-orientation
// narrow asset or centers as a solo row with a controlled max-width.
// Only true landscape media stretches to the full gallery width.
//
// Pairing rule: only pair when both assets share the same orientation
// (portrait+portrait or square+square). Portrait+square renders as two
// separate centered rows — different proportions on one line looks
// awkward. This is a small deterministic rule set, not a layout engine.
//
// Row types:
//   • wide          → one landscape asset, full content width.
//   • pair          → two consecutive same-orientation narrow assets.
//   • solo-portrait → one narrow asset (portrait or square), centered
//                     with an orientation-aware max-width so it reads
//                     as an intentional feature and never floats
//                     next to a phantom empty column.
// ---------------------------------------------------------------------------
function groupGalleryRows(gallery) {
  const rows = [];
  const isNarrow = (a) => a && a.orientation !== "landscape";
  let i = 0;
  while (i < gallery.length) {
    const a = gallery[i];
    if (isNarrow(a)) {
      const next = gallery[i + 1];
      if (next && isNarrow(next) && next.orientation === a.orientation) {
        rows.push({ layout: "pair", items: [a, next], keyStart: i });
        i += 2;
      } else {
        rows.push({ layout: "solo-portrait", items: [a], keyStart: i });
        i += 1;
      }
    } else {
      rows.push({ layout: "wide", items: [a], keyStart: i });
      i += 1;
    }
  }
  return rows;
}

// Pick a comfortable max-width for a centered narrow asset based on its
// natural shape + media kind. 9:16 vertical Story videos stay tight; A3
// / 4:5 portrait images breathe a little wider; square artwork wider
// still — none of them ever cross into landscape "wide" territory.
function narrowMaxWidth(asset) {
  if (asset.orientation === "square") return "max-w-[520px]";
  if (asset.kind === "image") return "max-w-[440px]";
  return "max-w-[380px]"; // portrait video (9:16)
}

const GalleryRow = ({ row, projectTitle, rowIndex }) => {
  if (row.layout === "solo-portrait") {
    const asset = row.items[0];
    return (
      <Reveal delay={rowIndex * 40} className="min-w-0">
        <div
          data-testid={`detail-row-${rowIndex}`}
          data-layout="solo-portrait"
          data-orientation={asset.orientation}
          data-kind={asset.kind}
          className="flex justify-center"
        >
          <div className={`w-full ${narrowMaxWidth(asset)}`}>
            <PortfolioMediaTile
              asset={asset}
              alt={`${projectTitle} — media ${row.keyStart + 1}`}
              testid={`detail-media-${row.keyStart}`}
            />
          </div>
        </div>
      </Reveal>
    );
  }
  if (row.layout === "pair") {
    // Portfolio Full-Width Pair (owner QA, Feb 2026) — a compatible
    // pair fills the FULL gallery content width so two 9:16 Stories or
    // two squares feel substantial. Solo narrow rows keep their
    // approved max-widths; only the pair container is uncapped.
    return (
      <Reveal delay={rowIndex * 40} className="min-w-0">
        <div
          data-testid={`detail-row-${rowIndex}`}
          data-layout="pair"
          data-orientation={row.items[0].orientation}
          className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6"
        >
          {row.items.map((asset, j) => (
            <PortfolioMediaTile
              key={`${asset.url}-${row.keyStart + j}`}
              asset={asset}
              alt={`${projectTitle} — media ${row.keyStart + j + 1}`}
              testid={`detail-media-${row.keyStart + j}`}
            />
          ))}
        </div>
      </Reveal>
    );
  }
  // wide (landscape) — full-width row
  const asset = row.items[0];
  return (
    <Reveal delay={rowIndex * 40} className="min-w-0">
      <div data-testid={`detail-row-${rowIndex}`} data-layout="wide">
        <PortfolioMediaTile
          asset={asset}
          alt={`${projectTitle} — media ${row.keyStart + 1}`}
          testid={`detail-media-${row.keyStart}`}
        />
      </div>
    </Reveal>
  );
};
